import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

// ========================================
// Voice Streaming Controller
// ========================================
// This controller manages real-time voice recording and transcription
// using the Web Audio API and ActionCable WebSockets
//
// Flow: User clicks mic → Capture audio → Stream to server → Receive transcription
// ========================================

export default class extends Controller {
  static targets = ["button", "stopButton", "statusText"]
  static values = { chatId: Number }

  // ========================================
  // CONFIGURATION CONSTANTS
  // ========================================
  // Audio Settings
  AUDIO_SAMPLE_RATE = 16000    // 16kHz for speech recognition
  AUDIO_CHANNELS = 1            // Mono audio
  AUDIO_BUFFER_SIZE = 4096      // Buffer size for fallback processor

  // Audio Format
  AUDIO_FORMAT = {
    format: 'pcm16',
    sample_rate: 16000,
    channels: 1
  }

  // Microphone Settings
  MICROPHONE_CONFIG = {
    audio: {
      channelCount: 1,
      sampleRate: 16000,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true
    }
  }

  // ========================================
  // LIFECYCLE METHODS
  // ========================================
  connect() {
    this.initializeState()
  }

  disconnect() {
    this.cleanupResources()
  }

  initializeState() {
    // Recording state
    this.isRecording = false

    // Audio processing components
    this.mediaRecorder = null
    this.audioContext = null
    this.audioProcessor = null

    // WebSocket channel
    this.channel = null
    this.channelConnected = false

    // Audio data storage
    this.audioChunks = []
    this.audioQueue = []
  }

  cleanupResources() {
    this.stopRecording()
    if (this.channel) {
      this.channel.unsubscribe()
      this.channel = null
      this.channelConnected = false
    }
    this.audioQueue = []
  }

  // ========================================
  // USER INTERACTION
  // ========================================

  // Main entry point - user clicks the microphone button
  async toggleRecording() {
    if (this.isRecording) {
      this.handleStopRecording()
    } else {
      await this.handleStartRecording()
    }
  }

  // ========================================
  // RECORDING MANAGEMENT
  // ========================================

  async handleStartRecording() {
    try {
      // Step 1: Reset for new recording
      this.resetTranscriptionState()

      // Step 2: Get microphone access
      const audioStream = await this.getMicrophoneAccess()

      // Step 3: Setup WebSocket connection
      this.setupWebSocketChannel()

      // Step 4: Setup audio processing pipeline
      await this.setupAudioPipeline(audioStream)

      // Step 5: Update UI to show recording
      this.setRecordingState(true)

    } catch (error) {
      this.handleRecordingError(error)
    }
  }

  handleStopRecording() {
    if (!this.isRecording) return

    // Step 1: Stop all audio processing
    this.stopAudioProcessing()

    // Step 2: Clean up media recorder
    this.cleanupMediaRecorder()

    // Step 3: Notify server we're done
    this.notifyServerStopped()

    // Step 4: Clean up WebSocket channel
    this.cleanupWebSocketChannel()

    // Step 5: Update UI to idle state
    this.setRecordingState(false)
  }

  // ========================================
  // AUDIO CAPTURE & PROCESSING
  // ========================================

  async getMicrophoneAccess() {
    return await navigator.mediaDevices.getUserMedia(this.MICROPHONE_CONFIG)
  }

  async setupAudioPipeline(stream) {
    // Create audio context with correct sample rate
    this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
      sampleRate: this.AUDIO_SAMPLE_RATE
    })

    // Create source from microphone stream
    const audioSource = this.audioContext.createMediaStreamSource(stream)

    // Try modern AudioWorklet, fallback to ScriptProcessor if needed
    try {
      await this.setupModernAudioProcessor(audioSource)
    } catch (error) {
      console.warn("AudioWorklet not available, using fallback:", error)
      this.setupFallbackAudioProcessor(audioSource)
    }
  }

  async setupModernAudioProcessor(audioSource) {
    // Load the audio worklet module
    await this.audioContext.audioWorklet.addModule('/audio-processor.js')

    // Create the processor node
    this.audioProcessor = new AudioWorkletNode(this.audioContext, 'audio-processor')

    // Handle processed audio chunks
    this.audioProcessor.port.onmessage = (event) => {
      if (event.data.audio) {
        this.handleAudioChunk(event.data.audio)
      }
    }

    // Connect microphone → processor
    audioSource.connect(this.audioProcessor)

    // Start processing
    this.audioProcessor.port.postMessage({ command: 'start' })
  }

  setupFallbackAudioProcessor(audioSource) {
    // Create legacy script processor
    const processor = this.audioContext.createScriptProcessor(
      this.AUDIO_BUFFER_SIZE, 1, 1
    )

    // Process audio in chunks
    processor.onaudioprocess = (event) => {
      if (!this.isRecording) return

      const audioData = event.inputBuffer.getChannelData(0)
      const pcm16Data = this.convertToPCM16(audioData)
      this.handleAudioChunk(pcm16Data)
    }

    // Connect microphone → processor → destination
    audioSource.connect(processor)
    processor.connect(this.audioContext.destination)
    this.audioProcessor = processor
  }

  handleAudioChunk(audioData) {
    if (this.channelConnected) {
      this.sendAudioToServer(audioData)
    } else {
      // Queue audio until connection is ready
      this.audioQueue.push(audioData)
    }
  }

  // ========================================
  // AUDIO FORMAT CONVERSION
  // ========================================

  convertToPCM16(float32Array) {
    const pcm16 = new Int16Array(float32Array.length)

    for (let i = 0; i < float32Array.length; i++) {
      // Clamp to [-1, 1] range
      const sample = Math.max(-1, Math.min(1, float32Array[i]))
      // Convert to 16-bit integer
      pcm16[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF
    }

    return pcm16.buffer
  }

  convertToBase64(arrayBuffer) {
    const bytes = new Uint8Array(arrayBuffer)
    let binaryString = ''

    for (let i = 0; i < bytes.length; i++) {
      binaryString += String.fromCharCode(bytes[i])
    }

    return btoa(binaryString)
  }

  // ========================================
  // WEBSOCKET COMMUNICATION
  // ========================================

  setupWebSocketChannel() {
    this.channel = consumer.subscriptions.create(
      {
        channel: "VoiceChannel",
        chat_id: this.chatIdValue
      },
      {
        connected: () => this.onWebSocketConnected(),
        disconnected: () => this.onWebSocketDisconnected(),
        received: (data) => this.onWebSocketMessage(data)
      }
    )
  }

  onWebSocketConnected() {
    // Notify server to start streaming session
    this.channel.perform("start_streaming", {
      user_agent: navigator.userAgent,
      audio_config: this.AUDIO_FORMAT
    })
  }

  onWebSocketDisconnected() {
    // Connection lost - handled by error states
  }

  onWebSocketMessage(data) {
    // Route messages based on type
    const messageHandlers = {
      'session_started': () => this.handleSessionStarted(),
      'partial_transcription': () => this.handlePartialTranscription(data.text),
      'final_transcription': () => this.handleFinalTranscription(data.text),
      'error': () => this.handleServerError(data.message),
      'status_update': () => this.handleStatusUpdate(data.status),
      'session_stopped': () => this.handleSessionStopped()
    }

    const handler = messageHandlers[data.type]
    if (handler) {
      handler()
    }
  }

  sendAudioToServer(audioBuffer) {
    if (!this.channel) return

    const base64Audio = this.convertToBase64(audioBuffer)
    this.channel.perform("send_audio", { audio: base64Audio })
  }

  notifyServerStopped() {
    if (this.channel) {
      this.channel.perform("stop_streaming")
    }
  }

  cleanupWebSocketChannel() {
    if (this.channel) {
      this.channel.unsubscribe()
      this.channel = null
      this.channelConnected = false
    }
    // Clear any queued audio
    this.audioQueue = []
  }

  // ========================================
  // TRANSCRIPTION HANDLING
  // ========================================

  resetTranscriptionState() {
    // Clear any previous transcription state
    this.channelConnected = false
    this.audioQueue = []
  }

  handlePartialTranscription() {
    // BytePlus rarely sends partial results with result_type: 'full'
    // We ignore them to avoid confusion
  }

  handleFinalTranscription(text) {
    // Skip empty results (silence/pauses)
    if (!text || text === "") {
      return
    }

    // BytePlus with result_type: 'full' sends COMPLETE transcription
    // Update the chat input with the full text
    this.updateChatInput(text)
    this.sendTranscriptionToBackend(text)
  }


  updateChatInput(text) {
    const messageInput = document.querySelector('[data-chat-form-target="input"]')

    if (messageInput) {
      messageInput.value = text
      messageInput.focus()
      // Trigger input event for any listeners
      messageInput.dispatchEvent(new Event('input', { bubbles: true }))
    }
  }

  sendTranscriptionToBackend(text) {
    if (this.channel) {
      this.channel.perform("update_transcription", {
        transcription: text
      })
    }
  }

  // ========================================
  // UI UPDATES
  // ========================================

  setRecordingState(isRecording) {
    this.isRecording = isRecording
    this.updateUI(isRecording ? "recording" : "idle")
  }

  updateUI(status) {
    if (!this.hasButtonTarget) return

    const uiStates = {
      'idle': () => {
        this.showStreamButton()
        this.hideStopButton()
        this.hideStatusText()
      },
      'recording': () => {
        this.hideStreamButton()
        this.showStopButton()
        this.showStatusText("Transcribing...")
      },
      'connected': () => {
        this.showStatusText("Transcribing...")
      },
      'connecting': () => {
        this.showStatusText("Connecting...")
      },
      'disconnected': () => {
        this.hideStatusText()
      }
    }

    const stateHandler = uiStates[status]
    if (stateHandler) {
      stateHandler()
    }
  }

  showStreamButton() {
    if (this.hasButtonTarget) {
      this.buttonTarget.classList.remove('hidden')
    }
  }

  hideStreamButton() {
    if (this.hasButtonTarget) {
      this.buttonTarget.classList.add('hidden')
    }
  }

  showStopButton() {
    if (this.hasStopButtonTarget) {
      this.stopButtonTarget.classList.remove('hidden')
    }
  }

  hideStopButton() {
    if (this.hasStopButtonTarget) {
      this.stopButtonTarget.classList.add('hidden')
    }
  }

  showStatusText(message) {
    if (this.hasStatusTextTarget) {
      this.statusTextTarget.textContent = message
      this.statusTextTarget.classList.remove('hidden')
    }
  }

  hideStatusText() {
    if (this.hasStatusTextTarget) {
      this.statusTextTarget.classList.add('hidden')
    }
  }

  // ========================================
  // ERROR HANDLING
  // ========================================

  handleRecordingError(error) {
    console.error("Failed to start recording:", error)
    this.showError("Failed to access microphone. Please check permissions.")
  }

  handleServerError(message) {
    this.showError(message)
    this.stopRecording()
  }

  showError(message) {
    console.error("Voice streaming error:", message)
    this.showStatusText(`Error: ${message}`)

    // Show as notification if Turbo is available
    if (window.Turbo) {
      const event = new CustomEvent("turbo:submit-end", {
        detail: {
          success: false,
          error: message
        }
      })
      document.dispatchEvent(event)
    }
  }

  // ========================================
  // CLEANUP METHODS
  // ========================================

  stopAudioProcessing() {
    if (this.audioProcessor) {
      this.audioProcessor.port?.postMessage({ command: 'stop' })
      this.audioProcessor.disconnect()
      this.audioProcessor = null
    }

    if (this.audioContext) {
      this.audioContext.close()
      this.audioContext = null
    }
  }

  cleanupMediaRecorder() {
    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      this.mediaRecorder.stop()
      this.mediaRecorder.stream.getTracks().forEach(track => track.stop())
      this.mediaRecorder = null
    }
  }

  // ========================================
  // MESSAGE HANDLERS
  // ========================================

  handleSessionStarted() {
    this.channelConnected = true
    this.updateUI("connected")

    // Send any queued audio
    this.flushAudioQueue()
  }

  flushAudioQueue() {
    while (this.audioQueue.length > 0) {
      const audioData = this.audioQueue.shift()
      this.sendAudioToServer(audioData)
    }
  }

  handleStatusUpdate(status) {
    this.updateUI(status)
  }

  handleSessionStopped() {
    // Session ended - no specific action needed
  }

  // Alias for public API
  startRecording() {
    return this.handleStartRecording()
  }

  stopRecording() {
    return this.handleStopRecording()
  }
}
